import { Input } from '@creditornot/cb-input';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import TimeMenu, { TimeMenuElement } from './TimeMenu';
import { formatMinutes, parseMinutes } from './utils';

export type Props = {
  className?: string;
  value: number;
  onChange: (value: number) => void;
  menuPlacement?: 'top' | 'bottom';
  disabled?: boolean;
};

const DAY_DURATION_MIN = 24 * 60;

const Root = styled.div`
  position: relative;
`;

const Menu = styled(TimeMenu)<{ placement: 'top' | 'bottom' }>`
  position: absolute;
  left: 0;
  z-index: 3;
  width: 136px;
  max-height: 188px;

  ${({ placement }) =>
    placement === 'bottom'
      ? css`
          top: calc(100% + 4px);
        `
      : css`
          bottom: calc(100% + 4px);
        `}
`;

const isNode = (eventTarget: any): eventTarget is Node => !!eventTarget && !!eventTarget.nodeName;

const TimePicker = ({ className, onChange, value, menuPlacement = 'bottom', disabled = false }: Props) => {
  if (value < 0 || value >= DAY_DURATION_MIN) {
    throw new Error('Value must be between 0 (inclusive) and 1440 (exclusive)');
  }

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const timerDropdownRef = useRef<TimeMenuElement | null>(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatMinutes(value));

  useLayoutEffect(() => {
    setInputValue((prevInputValue: string) =>
      parseMinutes(prevInputValue) === value ? prevInputValue : formatMinutes(value),
    );
  }, [value]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleBlur = (event: FocusEvent) => {
      if (!rootRef.current) {
        return;
      }

      if (
        isNode(event.target) &&
        rootRef.current.contains(event.target) &&
        !(isNode(event.relatedTarget) && rootRef.current.contains(event.relatedTarget))
      ) {
        setOpen(false);
        const newValue = parseMinutes(inputValue);
        if (newValue === null) {
          setInputValue(formatMinutes(value));
        } else {
          setInputValue(formatMinutes(newValue));
        }

        if (newValue !== null && newValue !== value) {
          onChange(newValue);
        }
      }
    };
    window.addEventListener('focusout', handleBlur);
    return () => {
      window.removeEventListener('focusout', handleBlur);
    };
  }, [inputValue, value, onChange]);

  return (
    <Root
      className={className}
      ref={rootRef}
      onKeyDown={event => {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setOpen(true);
          if (timerDropdownRef.current) {
            timerDropdownRef.current.previous();
          }
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setOpen(true);
          if (timerDropdownRef.current) {
            timerDropdownRef.current.next();
          }
        }

        if (event.key === 'Enter') {
          setOpen(false);
          const newValue = parseMinutes(inputValue);
          if (newValue === null) {
            setInputValue(formatMinutes(value));
          } else {
            onChange(newValue);
            setInputValue(formatMinutes(newValue));
          }
        }
      }}
    >
      <Input
        ref={inputElementRef}
        value={inputValue}
        disabled={disabled}
        onChange={event => {
          setInputValue(event.target.value);
          setOpen(true);
        }}
        onClick={() => {
          setOpen(true);
        }}
        onFocus={event => {
          event.target.select();
        }}
      />
      {open && (
        <Menu
          placement={menuPlacement}
          ref={timerDropdownRef}
          searchString={inputValue}
          onSearchStringChange={newInputValue => {
            setInputValue(newInputValue);
          }}
          onSelectTime={minutes => {
            setOpen(false);
            setInputValue(formatMinutes(minutes));
            onChange(minutes);
            if (inputElementRef.current) {
              inputElementRef.current.focus();
            }
          }}
        />
      )}
    </Root>
  );
};

export default TimePicker;
